import React, { useState } from 'react';
import { DillButton, DillModal } from '../../../general/Dill';
import { openDillToast } from '../../../../utils/helpers';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getSupplierCreditApplication,
  shareApplication,
} from '../../../../redux/services/creditApplicationsService';
import { ShareNetwork } from 'phosphor-react';

const ShareApplicationModal = ({
  open,
  handleClose,
  creditApplicationId,
  applicationHistoryId,
}: {
  open: boolean;
  handleClose: () => void;
  creditApplicationId: string;
  applicationHistoryId?: string | null;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((globalState) => globalState.auth);
  const currentSupplier = useAppSelector((globalState) => globalState.auth.currentSupplier);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorEmail, setErrorEmail] = useState('');
  const [email, setEmail] = useState('');

  const isValidEmail = (txt: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(txt);
  };

  const handleCloseModal = () => {
    setLoading(false);
    setMessage('');
    setEmail('');
    setErrorMessage('');
    setErrorEmail('');
    handleClose();
  };

  const handleShare = async () => {
    let hasError = false;

    if (!email.trim()) {
      setErrorEmail('Email is required.');
      hasError = true;
    } else if (!isValidEmail(email)) {
      setErrorEmail('Enter a valid email address.');
      hasError = true;
    }

    if (!message.trim()) {
      setErrorMessage('Message is required.');
      hasError = true;
    }

    if (hasError) return;

    try {
      setLoading(true);

      const result = await dispatch(
        shareApplication({
          supplierId: currentSupplier?.id ?? '',
          message,
          email,
          creditApplicationId,
          ...(applicationHistoryId ? { applicationHistoryId } : {}),
        })
      );

      if (result.type.includes('fulfilled')) {
        openDillToast({
          type: 'SUCCESS',
          message: 'Successfully shared application.',
        });

        dispatch(
          getSupplierCreditApplication({
            supplierId: currentSupplier?.id || '',
            creditApplicationId,
            hashedType: 'encrypted',
            fetchType: 'detailed',
            skipLoading: 'true',
          })
        );

        handleCloseModal();
      } else {
        openDillToast({
          type: 'ERROR',
          message: 'Failed to share application.',
        });
      }
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message: error?.message || 'Failed to share application.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Share Application"
      footer={
        <div className="flex justify-end w-full gap-2">
          <DillButton label="Close" variant="secondary" onClick={handleCloseModal} />
          <DillButton
            label="Share"
            onClick={handleShare}
            isLoading={loading}
            isDisabled={loading}
            icon={<ShareNetwork size={16} />}
          />
        </div>
      }>
      <div className="flex flex-col gap-3">
        <AppInputField
          label="Email"
          id="email"
          placeholder="Enter Email"
          value={email}
          onTextChange={(text) => {
            setEmail(text);
            setErrorEmail('');
          }}
          isRequired
          errorText={errorEmail}
        />
        <AppInputField
          label="Message"
          id="message"
          placeholder="Enter Message"
          inputType="textarea"
          value={message}
          onTextChange={(text) => {
            setMessage(text);
            setErrorMessage('');
          }}
          isRequired
          errorText={errorMessage}
        />
      </div>
    </DillModal>
  );
};

export default ShareApplicationModal;
